const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX_ORIGINAL_SIZE_BYTES = 20 * 1024 * 1024;
const MAX_OPTIMIZED_SIZE_BYTES = 5 * 1024 * 1024;

const readImageElement = (file) =>
  new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };

    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Unable to read image file"));
    };

    image.src = objectUrl;
  });

const convertCanvasToBlob = (canvas, type, quality) =>
  new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), type, quality);
  });

const replaceExtension = (filename, extension) => {
  const base = filename.includes(".") ? filename.slice(0, filename.lastIndexOf(".")) : filename;
  return `${base}.${extension}`;
};

export const validateImageFile = (file) => {
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    throw new Error("invalid_type");
  }

  if (file.size > MAX_ORIGINAL_SIZE_BYTES) {
    throw new Error("too_large_original");
  }
};

export const optimizeImageFile = async (file) => {
  validateImageFile(file);

  if (file.type === "image/webp" && file.size <= MAX_OPTIMIZED_SIZE_BYTES) {
    return file;
  }

  const image = await readImageElement(file);
  const maxDimension = 1920;
  const scale = Math.min(1, maxDimension / Math.max(image.width, image.height));
  const width = Math.max(1, Math.round(image.width * scale));
  const height = Math.max(1, Math.round(image.height * scale));

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext("2d", { alpha: true });
  if (!context) {
    throw new Error("compression_failed");
  }

  context.drawImage(image, 0, 0, width, height);

  const qualitySteps = [0.88, 0.8, 0.72, 0.64, 0.56];
  let bestBlob = null;

  for (const quality of qualitySteps) {
    const blob = await convertCanvasToBlob(canvas, "image/webp", quality);
    if (!blob) {
      continue;
    }

    if (!bestBlob || blob.size < bestBlob.size) {
      bestBlob = blob;
    }

    if (blob.size <= MAX_OPTIMIZED_SIZE_BYTES) {
      return new File([blob], replaceExtension(file.name, "webp"), {
        type: "image/webp",
        lastModified: Date.now(),
      });
    }
  }

  if (!bestBlob || bestBlob.size > MAX_OPTIMIZED_SIZE_BYTES) {
    throw new Error("too_large_after_optimization");
  }

  return new File([bestBlob], replaceExtension(file.name, "webp"), {
    type: "image/webp",
    lastModified: Date.now(),
  });
};

export const getImageUploadErrorMessageKey = (errorCode) => {
  if (errorCode === "invalid_type") return "invalidType";
  if (errorCode === "too_large_original") return "tooLargeOriginal";
  if (errorCode === "too_large_after_optimization") return "tooLargeAfterOptimization";
  return "optimizationFailed";
};
